import React, { useCallback } from 'react';
import type { SurveyStepProps } from '../types';
import { StyledSurveyButtonWrapper, StyledSurveyContent, StyledSurveyWrapper, Survey } from '@shared/components/Survey';
import { useNavButtonStyles } from '../../hooks/useNavButtonStyles';
import { SpacingStack } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { STEP_MINIMUM_HEIGHT } from '../consts';

export const EventFormSurveyStep = (props: SurveyStepProps) => {
  const { formData, onNext, onBack, hasPrev, formState, setFormState, colors: guestSiteColors } = props;
  const { tq } = useTranslation('eventFormDisplay');

  const updateFormStateWithAnswers = useCallback(
    (answers: Record<string, unknown>) => {
      // Convert answers to string record for formValues
      const stringAnswers: Record<string, string> = {};
      Object.entries(answers).forEach(([key, value]) => {
        stringAnswers[key] = String(value);
      });

      // Update form state with survey answers
      setFormState({
        ...formState,
        formValues: {
          ...formState.formValues,
          ...stringAnswers,
        },
      });
    },
    [formState, setFormState],
  );

  const handleComplete = useCallback(
    (answers: Record<string, unknown>) => {
      updateFormStateWithAnswers(answers);
      onNext();
    },
    [updateFormStateWithAnswers, onNext],
  );

  const { solidButtonStyles, outlineButtonStyles } = useNavButtonStyles(guestSiteColors);

  return (
    <SpacingStack>
      <Survey
        config={{
          ...formData,
          initialValues: formState.formValues ?? {},
          buttons: {
            submitLabel: tq`Next`,
            nextLabel: tq`Next`,
            nextProps: solidButtonStyles,
            submitProps: solidButtonStyles,
            backProps: { variant: 'outline', ...outlineButtonStyles },
            showBackOnFirstStep: hasPrev,
          },
        }}
        styleOverrides={{
          // !important required because Survey inner sets padding via a media query.
          // we do not want padding so that we can pad things ourselves.
          padding: '0 !important',
          [StyledSurveyWrapper]: {
            padding: '0',
            rowGap: 5,
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          },
          [StyledSurveyContent]: {
            padding: '0',
            rowGap: 5,
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            minHeight: STEP_MINIMUM_HEIGHT,
          },
          [StyledSurveyButtonWrapper]: {
            width: '100%',
            columnGap: 6,
          },
        }}
        onComplete={handleComplete}
        onFirstStepBack={onBack}
        telemetryCategory={'eventForm'}
        telemetryPagePrefix={'admin'}
      />
    </SpacingStack>
  );
};
